'use client';

/**
 * TmxProfiler — ThreatMetrix (TMX) device profiling trigger.
 *
 * This client component performs the DOM-side half of TMX profiling (the pure
 * URL/config helpers live in `@workspace/lib/utils/tmx-profiling`). It injects
 * a vendored ThreatMetrix loader script (served from the app's public/) and then invokes device
 * profiling against the SAME `tmxSessionId` that ClientBootstrap seeds into the
 * session store and that is later sent to the backend as `tmxSessionId`.
 *
 * Why this matters (CST-25471): the backend's TMX Session Query can only
 * resolve to a real, profiled device if the browser has actually run profiling
 * against that exact session id. When profiling never fires — or fires against
 * a different id — the query comes back as a "Non-Profiled Device" (-100),
 * which fails the downstream fraud/threat checks orchestrated by
 * LighteningService. Running profiling here, keyed on the shared
 * `tmxSessionId`, is what fixes that.
 *
 * How it works:
 *  - The loader is a vendored ThreatMetrix loader library served from the
 *    app's `public/` at `/tmx/tmx-loader.js`. It defines the obfuscated global
 *    `window.asset81hasf`, whose `profile(domain, orgId, sessionId, pageId)`
 *    method starts device profiling; we invoke it with the resolved `config`
 *    (domain, orgId, pageId) and the shared session id.
 *  - The org id, domain and page id are resolved per-app from `NEXT_PUBLIC_*`
 *    env vars via `resolveTmxProfilingConfig` in the app layer and passed in
 *    as `config`.
 *  - Route allow-listing is done in the app layer and surfaced here as the
 *    `active` prop; this component does no route matching itself.
 *  - Backend DecisionCallTmx gate (CST-25471): profiling only runs when the
 *    Stage 1 (person-details) decision response opted in. The FE persists that
 *    flag to the session store as `decisionCallTmx` and this component gates on
 *    it (=== 1, tolerant of the legacy string "1"), mirroring the legacy
 *    Sitecore DecisionCallTmx behaviour.
 *
 * Safety: profiling is entirely optional and must never break the journey.
 * Every guard below is a silent skip (return / render null) rather than a
 * throw. Profiling runs at most once per session id (module-level idempotency
 * guard) so repeated visits to allow-listed routes within the same page load
 * do not re-profile.
 *
 * This component is read-only with respect to the session store — it never
 * writes or mutates `tmxSessionId`.
 */
import { useEffect } from 'react';

import {
  SESSION_STORE_KEYS,
  useSessionStore,
} from '@workspace/lib/hooks/store/use-session-store';
import {
  buildTmxProfilingIframeUrl,
  isTmxProfilingConfigured,
  type TmxProfilingConfig,
} from '@workspace/lib/utils/tmx-profiling';

/**
 * The id used for the injected loader `<script>` so we never inject it twice.
 */
const TMX_LOADER_SCRIPT_ID = 'tmx-tags-loader';

/**
 * Source of the vendored TMX loader library, served from each app's `public/`
 * at `/tmx/tmx-loader.js`. It defines `window.asset81hasf`.
 */
const TMX_LOADER_SCRIPT_SRC = '/tmx/tmx-loader.js';

/**
 * Module-level (file-scope) idempotency guard.
 *
 * This deliberately lives OUTSIDE the component so it survives re-renders and
 * client-side (SPA) navigations within the same page load. It records the
 * session id we have already profiled, ensuring profiling runs once per
 * session id rather than on every allow-listed route visit.
 */
let profiledSessionId: string | null = null;

export interface TmxProfilerProps {
  /** Resolved per-app via `resolveTmxProfilingConfig` in the app layer. */
  config: TmxProfilingConfig;
  /**
   * True only when the current route is in the app's TMX allow-list. Route
   * matching is performed in the app layer, NOT here.
   */
  active: boolean;
}

/**
 * Narrows an unknown session-store value to a non-empty string, or returns
 * `null`. The store can hold `string | number | boolean | Record | undefined`,
 * so we guard the type here rather than trusting the caller.
 */
function toNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function TmxProfiler({ config, active }: TmxProfilerProps) {
  const { getFromSessionStore } = useSessionStore();

  const sessionId = toNonEmptyString(
    getFromSessionStore(SESSION_STORE_KEYS.tmxSessionId),
  );

  const decisionCallTmx = getFromSessionStore(
    SESSION_STORE_KEYS.decisionCallTmx,
  );

  // The backend's Stage 1 decision response sets this flag; profiling only
  // runs when it indicates "1" (number 1 or the legacy string "1").
  const isTmxDecisionEnabled = decisionCallTmx === 1 || decisionCallTmx === '1';

  useEffect(() => {
    // 1. Environment / activation / configuration guards — all silent skips.
    if (typeof window === 'undefined') return;
    if (!active) return;
    if (!isTmxProfilingConfigured(config)) return;

    // 2. We need a valid, non-empty session id. ClientBootstrap seeds this; if
    //    it is somehow absent we skip rather than invent one.
    if (!sessionId) return;

    // 2b. Backend gate (CST-25471): only profile when the Stage 1 decision
    //     response opted in. This mirrors the legacy Sitecore DecisionCallTmx
    //     behaviour.
    if (!isTmxDecisionEnabled) return;

    // 3. Idempotency: profile once per session id, not per route visit.
    if (profiledSessionId === sessionId) return;

    /**
     * Invokes device profiling if the loader-defined global is present, then
     * records the session id so we never profile it again this page load.
     * Re-checks the configuration guard defensively before firing.
     */
    const runProfile = () => {
      if (!isTmxProfilingConfigured(config)) return;
      if (profiledSessionId === sessionId) return;

      const tmx = window.asset81hasf;
      if (!tmx) return;

      // The loader global is third-party code; if `profile` throws we must
      // swallow it so the exception never escapes the effect/onload handler.
      // We only record the session id once the call returns cleanly, so a
      // failure leaves the id un-profiled and a later attempt can retry.
      try {
        tmx.profile(config.domain, config.orgId, sessionId, config.pageId);
        profiledSessionId = sessionId;
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[TMX] device profiling call failed', error);
        }
      }
    };

    // If the loader global is already available (e.g. the script was injected
    // on an earlier navigation and has since loaded), profile immediately.
    if (window.asset81hasf) {
      runProfile();
      return;
    }

    // 4. Otherwise ensure the loader script is present exactly once, and run
    //    profiling once it has loaded. The script is async, so we wait on its
    //    onload rather than polling.
    const existing = document.getElementById(
      TMX_LOADER_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existing) {
      // Script already injected but the global was not ready above — wait for
      // its load event. (If it had already fired, the early `asset81hasf`
      // check above would have handled it.)
      existing.addEventListener('load', runProfile);
      return () => existing.removeEventListener('load', runProfile);
    }

    const script = document.createElement('script');
    script.id = TMX_LOADER_SCRIPT_ID;
    script.src = TMX_LOADER_SCRIPT_SRC;
    script.async = true;
    script.onload = runProfile;
    document.head.appendChild(script);
  }, [config, active, sessionId, isTmxDecisionEnabled]);

  // 6. Emit the <noscript> fallback iframe for parity with the legacy markup.
  //    Its contents only render when JavaScript is disabled, but we keep it for
  //    behavioural parity. It is a non-content profiling iframe, so it is
  //    aria-hidden and removed from tab order to keep it out of the
  //    accessibility tree and keyboard navigation. Only render when profiling
  //    is active, configured and we have a valid session id; otherwise render
  //    nothing.
  if (
    !active ||
    !isTmxProfilingConfigured(config) ||
    !sessionId ||
    !isTmxDecisionEnabled
  ) {
    return null;
  }

  const iframeSrc = buildTmxProfilingIframeUrl(config, sessionId);

  return (
    <noscript>
      <iframe
        title='ThreatMetrix device profiling (no-JS fallback)'
        aria-hidden={true}
        tabIndex={-1}
        src={iframeSrc}
        style={{
          width: '100px',
          height: '100px',
          border: 0,
          position: 'absolute',
          top: '-5000px',
        }}
      />
    </noscript>
  );
}
